import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Eraser,
  Mic,
  Pencil,
  Play,
  Square,
  Trash2,
  Undo2,
  Video,
  X,
} from "lucide-react";
import {
  containLayoutStyle,
  containRect,
  pointerToSvgViewBoxNorm,
  stageNormToFramePx,
} from "../utils/videoContainCoords";
import "./VideoFrameAnnotator.css";

const COLORS = ["#facc15", "#fb7185", "#38bdf8", "#4ade80", "#ffffff"];
const MAX_RECORD_MS = 30000;

function newId() {
  return `an_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

function pointPx(pt, stroke, w, h, metrics) {
  const framePoint = stroke?.coordSpace === "frame" || pt?.space === "frame";
  if (framePoint || !metrics?.frame) {
    return { x: pt.x * w, y: pt.y * h };
  }
  const local = stageNormToFramePx(
    pt.x,
    pt.y,
    metrics.stageW,
    metrics.stageH,
    metrics.videoW,
    metrics.videoH
  );
  const rect = metrics.rect;
  return {
    x: local.x * (w / Math.max(1, rect.w)),
    y: local.y * (h / Math.max(1, rect.h)),
  };
}

function paintStrokes(canvas, strokes, metrics) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  const w = canvas.clientWidth || 1;
  const h = canvas.clientHeight || 1;
  const bufW = Math.round(w * dpr);
  const bufH = Math.round(h * dpr);
  if (canvas.width !== bufW || canvas.height !== bufH) {
    canvas.width = bufW;
    canvas.height = bufH;
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);
  ctx.globalCompositeOperation = "source-over";
  for (const stroke of strokes || []) {
    const pts = stroke.points || [];
    if (!pts.length) continue;
    const start = pointPx(pts[0], stroke, w, h, metrics);
    ctx.save();
    ctx.globalCompositeOperation = stroke.erase ? "destination-out" : "source-over";
    ctx.beginPath();
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = stroke.color || "#facc15";
    const widthNorm = Number(stroke.widthNorm);
    ctx.lineWidth =
      widthNorm > 0 ? widthNorm * h : stroke.width || 3;
    ctx.moveTo(start.x, start.y);
    for (let i = 1; i < pts.length; i += 1) {
      const next = pointPx(pts[i], stroke, w, h, metrics);
      ctx.lineTo(next.x, next.y);
    }
    if (pts.length === 1) ctx.lineTo(start.x + 0.4, start.y);
    ctx.stroke();
    ctx.restore();
  }
}

export function VideoFrameAnnotator({
  active = false,
  onClose,
  notes = [],
  onChangeNotes,
  currentTimeSec = 0,
  isPlaying = false,
  onPause,
  playbackStrokes = null,
  onStrokeCommitted,
  onVoiceReady,
  onTranscriptReady,
  onDemoReady,
  onPrepareFeedback,
  onBeginScreenRecording,
  onEndScreenRecording,
  onFeedbackRecordingChange,
  videoWidth = 0,
  videoHeight = 0,
  contentTransform = "",
  toolbarRoot = null,
  canReplayLast = false,
  onReplayLast,
  t = (k, d) => d || k,
}) {
  const canvasRef = useRef(null);
  const svgRef = useRef(null);
  const rootRef = useRef(null);
  const metricsRef = useRef(null);
  const drawingRef = useRef(false);
  const pointersRef = useRef(new Set());
  const strokeStartedAtRef = useRef(0);
  const [tool, setTool] = useState("pen");
  const [color, setColor] = useState(COLORS[0]);
  const [draft, setDraft] = useState([]);
  const [recording, setRecording] = useState(false);
  const [recordMode, setRecordMode] = useState(null);
  const [micArmed, setMicArmed] = useState(true);
  const [videoArmed, setVideoArmed] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [voiceUrl, setVoiceUrl] = useState("");
  const [transcript, setTranscript] = useState("");
  const [status, setStatus] = useState("");
  const [stageSize, setStageSize] = useState({ w: 0, h: 0 });
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(0);
  const speechRef = useRef(null);
  const transcriptRef = useRef("");
  const cueSentRef = useRef("");
  const captureStartedAtRef = useRef(0);
  const tMs = Math.round(Math.max(0, Number(currentTimeSec) || 0) * 1000);

  const frameNote =
    (notes || []).find((note) => Math.abs((note.tMs || 0) - tMs) <= 180) || null;
  const playbackNote = !active ? frameNote : null;
  const strokesOnScreen = () => {
    const existing = frameNote?.strokes || [];
    return [...existing, ...(draft || [])].filter((stroke) => stroke?.points?.length);
  };

  const fitted =
    stageSize.w > 2 &&
    stageSize.h > 2 &&
    Number(videoWidth) > 2 &&
    Number(videoHeight) > 2;
  const frameRect = fitted
    ? containRect(stageSize.w, stageSize.h, videoWidth, videoHeight)
    : null;
  metricsRef.current = fitted
    ? {
        frame: true,
        stageW: stageSize.w,
        stageH: stageSize.h,
        videoW: videoWidth,
        videoH: videoHeight,
        rect: frameRect,
      }
    : { frame: false };

  useEffect(() => {
    const root = rootRef.current;
    const stage = root?.parentElement;
    if (!stage) return undefined;
    const measure = () => {
      const w = stage.clientWidth || 0;
      const h = stage.clientHeight || 0;
      setStageSize((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(stage);
    return () => ro.disconnect();
  }, [active, Boolean(playbackNote), Boolean(playbackStrokes?.length)]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const strokes = active
      ? [...(frameNote?.strokes || []), ...draft]
      : playbackStrokes || playbackNote?.strokes || [];
    const paint = () => paintStrokes(canvas, strokes, metricsRef.current);
    paint();
    const ro = new ResizeObserver(paint);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [active, draft, frameNote, playbackNote, playbackStrokes, fitted, stageSize, videoWidth, videoHeight]);

  useEffect(() => {
    if (!active) {
      setDraft([]);
      setVoiceUrl("");
      setTranscript("");
      transcriptRef.current = "";
      setStatus("");
      setMicArmed(true);
      setVideoArmed(false);
      setRecording(false);
      setRecordMode(null);
      setSecondsLeft(null);
    }
  }, [active]);

  const pointerPos = (event) => {
    const svg = svgRef.current;
    if (svg && metricsRef.current?.frame) {
      const pt = pointerToSvgViewBoxNorm(event, svg);
      return { x: pt.x, y: pt.y, at: Date.now(), space: "frame" };
    }
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const rw = rect.width || 1;
    const rh = rect.height || 1;
    return {
      x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rw)),
      y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rh)),
      at: Date.now(),
    };
  };

  const onPointerDown = (event) => {
    if (!active) return;
    if (isPlaying) {
      onPause?.();
      setStatus(
        t("feedbackPauseRequired", "Pause the video on a frame to draw feedback.")
      );
      return;
    }
    pointersRef.current.add(event.pointerId);
    if (pointersRef.current.size > 1) {
      event.preventDefault();
      if (drawingRef.current) {
        drawingRef.current = false;
        setDraft((prev) => prev.slice(0, -1));
      }
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (isPlaying) onPause?.();
    drawingRef.current = true;
    strokeStartedAtRef.current = Date.now();
    svgRef.current?.setPointerCapture?.(event.pointerId);
    const pt = pointerPos(event);
    const boxH = svgRef.current?.clientHeight || canvasRef.current?.clientHeight || 1;
    const pxWidth = tool === "eraser" ? 22 : 3.5;
    setDraft((prev) => [
      ...prev,
      {
        color,
        width: pxWidth,
        erase: tool === "eraser",
        ...(pt.space === "frame"
          ? { coordSpace: "frame", widthNorm: pxWidth / boxH }
          : {}),
        points: [pt],
      },
    ]);
  };

  const onPointerMove = (event) => {
    if (!active || !drawingRef.current || pointersRef.current.size > 1) return;
    const pt = pointerPos(event);
    setDraft((prev) => {
      if (!prev.length) return prev;
      const next = prev.slice();
      const last = next[next.length - 1];
      next[next.length - 1] = { ...last, points: [...last.points, pt] };
      return next;
    });
  };

  const endStroke = (event) => {
    if (event?.pointerId != null) pointersRef.current.delete(event.pointerId);
    if (!drawingRef.current) return;
    if (pointersRef.current.size > 0) {
      drawingRef.current = false;
      setDraft((prev) => prev.slice(0, -1));
      return;
    }
    drawingRef.current = false;
    setDraft((prev) => {
      const last = prev[prev.length - 1];
      if (last?.points?.length) {
        onStrokeCommitted?.({
          ...last,
          drawMs: Math.max(0, Date.now() - (strokeStartedAtRef.current || Date.now())),
        });
      }
      return prev;
    });
  };

  const publishTranscript = () => {
    window.setTimeout(() => {
      const text = transcriptRef.current;
      if (!text || cueSentRef.current === text) return;
      cueSentRef.current = text;
      onTranscriptReady?.(text);
    }, 700);
  };

  const stopSpeech = () => {
    const rec = speechRef.current;
    speechRef.current = null;
    if (!rec) return;
    try {
      rec.onresult = null;
      rec.stop();
    } catch {
      /* ignore */
    }
  };

  const armSpeech = () => {
    transcriptRef.current = "";
    setTranscript("");
    const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Ctor) return false;
    const rec = new Ctor();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = navigator.language || "fr-FR";
    rec.onresult = (event) => {
      let text = "";
      for (let i = 0; i < event.results.length; i += 1) {
        text += event.results[i][0]?.transcript || "";
      }
      transcriptRef.current = text.replace(/\s+/g, " ").trim();
      setTranscript(transcriptRef.current);
    };
    try {
      rec.start();
      speechRef.current = rec;
      return true;
    } catch {
      speechRef.current = null;
      return false;
    }
  };

  const startCapture = async (mode) => {
    if (recording) return;
    if (isPlaying) {
      onPause?.();
      setStatus(
        t("feedbackPauseRequired", "Pause the video on a frame to record feedback.")
      );
      return;
    }
    onPrepareFeedback?.({
      paused: true,
      strokes: strokesOnScreen(),
    });
    try {
      const stream = await navigator.mediaDevices.getUserMedia(
        mode === "video"
          ? {
              audio: true,
              video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 360 } },
            }
          : { audio: true }
      );
      let rec;
      try {
        rec =
          mode === "video"
            ? new MediaRecorder(stream, { videoBitsPerSecond: 700000 })
            : new MediaRecorder(stream);
      } catch {
        rec = new MediaRecorder(stream);
      }
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data?.size) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop());
        window.clearInterval(timerRef.current);
        setSecondsLeft(null);
        setRecordMode(null);
        stopSpeech();
        publishTranscript();
        const recordedMs = Math.max(
          0,
          Date.now() - (captureStartedAtRef.current || Date.now())
        );
        const blob = new Blob(chunksRef.current, {
          type: rec.mimeType || "audio/webm",
        });
        const limit = mode === "video" ? 8_000_000 : 2_500_000;
        if (blob.size > limit) {
          onFeedbackRecordingChange?.(false);
          setStatus(
            t("annotationVoiceTooLong", "Recording is too long. Keep it under 30s.")
          );
          return;
        }
        const reader = new FileReader();
        reader.onload = () => {
          const url = String(reader.result || "");
          const meta = { atMs: 0, durationMs: recordedMs };
          if (mode === "video") {
            if (url) onDemoReady?.(url, meta);
          } else if (url) {
            setVoiceUrl(url);
            onVoiceReady?.(url, meta);
          }
          onFeedbackRecordingChange?.(false);
        };
        reader.onerror = () => onFeedbackRecordingChange?.(false);
        reader.readAsDataURL(blob);
      };
      const heard = mode === "video" || micArmed ? armSpeech() : false;
      recRef.current = rec;
      captureStartedAtRef.current = Date.now();
      try {
        rec.start(200);
      } catch {
        rec.start();
      }
      setRecording(true);
      setRecordMode(mode);
      onFeedbackRecordingChange?.(true);
      if ((mode === "voice" || micArmed) && !heard) {
        setStatus(
          t(
            "annotationNoSpeech",
            "Voice is recording. This browser cannot turn it into text."
          )
        );
      }
      const startedAt = Date.now();
      setSecondsLeft(30);
      window.clearInterval(timerRef.current);
      timerRef.current = window.setInterval(() => {
        const left = Math.max(0, Math.ceil((MAX_RECORD_MS - (Date.now() - startedAt)) / 1000));
        setSecondsLeft(left);
        if (left <= 0) {
          window.clearInterval(timerRef.current);
          if (rec.state === "recording") rec.stop();
          setRecording(false);
        }
      }, 250);
    } catch {
      stopSpeech();
      setStatus(
        mode === "video"
          ? t("annotationVideoDenied", "Camera permission is required for a video demonstration.")
          : t("annotationMicDenied", "Microphone permission is required for voice feedback.")
      );
    }
  };

  const startSessionOnly = () => {
    if (recording) return;
    if (isPlaying) {
      onPause?.();
      setStatus(
        t("feedbackPauseRequired", "Pause the video on a frame to record feedback.")
      );
      return;
    }
    onPrepareFeedback?.({
      paused: true,
      strokes: strokesOnScreen(),
    });
    setRecording(true);
    setRecordMode("session");
    onFeedbackRecordingChange?.(true);
    setSecondsLeft(null);
  };

  const stopVoice = () => {
    window.clearInterval(timerRef.current);
    if (recRef.current && recRef.current.state === "recording") {
      try {
        recRef.current.requestData();
      } catch {
        /* ignore */
      }
      recRef.current.stop();
    } else if (recordMode === "session") {
      onFeedbackRecordingChange?.(false);
    }
    setRecording(false);
    setSecondsLeft(null);
    setRecordMode(null);
  };

  const toggleFeedback = () => {
    if (recording) {
      stopVoice();
      return;
    }
    if (isPlaying) {
      onPause?.();
      setStatus(
        t("feedbackPauseRequired", "Pause the video on a frame to record feedback.")
      );
      return;
    }
    if (videoArmed) {
      startCapture("video");
      return;
    }
    if (micArmed) {
      startCapture("voice");
      return;
    }
    startSessionOnly();
  };

  const saveNote = () => {
    const strokes = (draft || []).filter((s) => s.points?.length);
    if (!strokes.length && !voiceUrl) {
      setStatus(
        t("annotationEmpty", "Draw on the frame or record a voice note first.")
      );
      return;
    }
    const note = {
      id: newId(),
      tMs,
      strokes: strokes.map(({ color: c, width, widthNorm, erase, points, coordSpace }) => ({
        color: c,
        width,
        erase: Boolean(erase),
        ...(coordSpace === "frame" ? { coordSpace: "frame" } : {}),
        ...(Number(widthNorm) > 0 ? { widthNorm: Number(widthNorm) } : {}),
        points,
      })),
      voiceDataUrl: voiceUrl || null,
      transcript: transcriptRef.current || transcript || null,
      createdAt: new Date().toISOString(),
    };
    onChangeNotes?.([...(notes || []), note]);
    setDraft([]);
    setVoiceUrl("");
    setStatus(t("annotationSaved", "Feedback saved on this frame."));
  };

  const removePlayback = () => {
    if (!playbackNote) return;
    onChangeNotes?.((notes || []).filter((n) => n.id !== playbackNote.id));
  };

  if (!active && !playbackNote && !(playbackStrokes && playbackStrokes.length)) return null;

  const frameStyle = frameRect
    ? {
        ...containLayoutStyle(stageSize.w, stageSize.h, videoWidth, videoHeight),
        right: "auto",
        bottom: "auto",
        transform: contentTransform || undefined,
        transformOrigin: "center center",
      }
    : null;

  return (
    <div
      ref={rootRef}
      className={`video-annotator${frameStyle ? " video-annotator--frame" : ""}${
        active ? " is-drawing" : " is-playback"
      }${isPlaying ? " is-playing" : ""}`}
      style={frameStyle || undefined}
    >
      <canvas ref={canvasRef} className="video-annotator__canvas" />
      <svg
        ref={svgRef}
        className="video-annotator__hit"
        viewBox="0 0 1 1"
        preserveAspectRatio="none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endStroke}
        onPointerCancel={endStroke}
      >
        <rect width="1" height="1" fill="transparent" />
      </svg>
      {active
        ? createPortal(
            <div
              className="video-annotator__bar"
              onPointerDown={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="video-annotator__close"
                onClick={onClose}
                aria-label={t("close", "Close")}
              >
                <X size={16} />
              </button>
              <div className="video-annotator__rail">
                <div className="video-annotator__actions" role="group" aria-label={t("tools", "Tools")}>
                  <div className="video-annotator__group" role="group" aria-label={t("drawTools", "Draw")}>
                    <button
                      type="button"
                      className={tool === "pen" ? "is-on" : ""}
                      disabled={isPlaying}
                      onClick={() => setTool("pen")}
                      aria-label={t("draw", "Draw")}
                      title={t("draw", "Draw")}
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      type="button"
                      className={tool === "eraser" ? "is-on" : ""}
                      disabled={isPlaying}
                      onClick={() => setTool("eraser")}
                      aria-label={t("eraser", "Eraser")}
                      title={t("eraser", "Eraser")}
                    >
                      <Eraser size={16} />
                    </button>
                    <button
                      type="button"
                      disabled={isPlaying}
                      onClick={() => setDraft((d) => d.slice(0, -1))}
                      aria-label={t("undo", "Undo")}
                      title={t("undo", "Undo")}
                    >
                      <Undo2 size={16} />
                    </button>
                    <button
                      type="button"
                      disabled={isPlaying}
                      onClick={() => setDraft([])}
                      aria-label={t("clear", "Clear")}
                      title={t("clear", "Clear")}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <div
                    className="video-annotator__group"
                    role="group"
                    aria-label={t("recordTools", "Record")}
                  >
                    <button
                      type="button"
                      className={micArmed ? "is-on" : ""}
                      disabled={recording}
                      onClick={() => setMicArmed((v) => !v)}
                      aria-pressed={micArmed}
                      aria-label={t("toggleMic", "Microphone")}
                      title={t("toggleMic", "Microphone")}
                    >
                      <Mic size={16} />
                    </button>
                    <button
                      type="button"
                      className={videoArmed ? "is-on" : ""}
                      disabled={recording}
                      onClick={() => setVideoArmed((v) => !v)}
                      aria-pressed={videoArmed}
                      aria-label={t("toggleVideo", "Camera")}
                      title={t("toggleVideo", "Camera")}
                    >
                      <Video size={16} />
                    </button>
                    <button
                      type="button"
                      className={`video-annotator__record${recording ? " is-rec" : ""}`}
                      onClick={toggleFeedback}
                      aria-label={
                        recording
                          ? t("stopFeedback", "Stop")
                          : t("startFeedback", "Start")
                      }
                      title={
                        recording
                          ? t("stopFeedback", "Stop")
                          : isPlaying
                            ? t(
                                "feedbackPauseRequired",
                                "Pause the video on a frame to record feedback."
                              )
                            : t("startFeedback", "Start")
                      }
                    >
                      {recording ? (
                        <Square size={14} fill="currentColor" />
                      ) : (
                        <Play size={14} fill="currentColor" />
                      )}
                      <span>
                        {recording
                          ? t("stopFeedback", "Stop")
                          : t("startFeedback", "Start")}
                      </span>
                    </button>
                    {recording && secondsLeft != null ? (
                      <span className="video-annotator__timer" aria-live="polite">
                        {secondsLeft}s
                      </span>
                    ) : null}
                  </div>
                  <div
                    className="video-annotator__group"
                    role="group"
                    aria-label={t("replayTools", "Replay")}
                  >
                    <button
                      type="button"
                      className="video-annotator__replay"
                      disabled={recording || !canReplayLast}
                      onClick={() => {
                        if (!canReplayLast) return;
                        onClose?.();
                        onReplayLast?.();
                      }}
                      aria-label={t("replayLastFeedback", "Replay last")}
                      title={t("replayLastFeedback", "Replay last feedback")}
                    >
                      <Play size={14} />
                      <span>{t("replayShort", "Replay")}</span>
                    </button>
                    <button
                      type="button"
                      className="video-annotator__save"
                      disabled={isPlaying}
                      onClick={saveNote}
                    >
                      {t("saveFeedback", "Save")}
                    </button>
                  </div>
                </div>
                <div
                  className="video-annotator__colors"
                  role="group"
                  aria-label={t("colors", "Colors")}
                >
                  {COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`video-annotator__swatch${
                        color === c && tool === "pen" ? " is-on" : ""
                      }`}
                      style={{ background: c }}
                      disabled={isPlaying}
                      onClick={() => {
                        setColor(c);
                        setTool("pen");
                      }}
                      aria-label={c}
                    />
                  ))}
                </div>
              </div>
              {isPlaying ? (
                <p className="video-annotator__status">
                  {t(
                    "feedbackPauseRequired",
                    "Pause the video on a frame to give feedback."
                  )}
                </p>
              ) : null}
              {voiceUrl ? (
                <audio className="video-annotator__audio" src={voiceUrl} controls />
              ) : null}
              {transcript ? <p className="video-annotator__status">{transcript}</p> : null}
              {status ? <p className="video-annotator__status">{status}</p> : null}
            </div>,
            toolbarRoot || document.body
          )
        : playbackNote?.voiceDataUrl ? (
            <div className="video-annotator__playback">
              <audio src={playbackNote.voiceDataUrl} controls />
              <button
                type="button"
                onClick={removePlayback}
                aria-label={t("deleteAnnotation", "Delete annotation")}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ) : null}
    </div>
  );
}

export default VideoFrameAnnotator;
